import { useCallback, useRef } from "react"
import {
  BadgeElement,
  BadgeTemplate,
  BadgeTextData,
  BadgeFieldData,
  BadgeImageData,
  BadgeLineData,
  BadgeShapeData,
  BadgeQRData,
} from "sharedTypes/badge"
import restrictMovementToBounds from "components/CanvasEditor/lib/transform"
import { getTemplateDimensions, updateTemplateElements } from "../utils/badgeHelpers"
import { canLeaveBadge } from "../utils/constants"

// Type for handleElementChange updates: can be element properties or data properties
export type ElementUpdate = Partial<Omit<BadgeElement, "id" | "type" | "data">> &
  Partial<BadgeTextData | BadgeFieldData |
  BadgeImageData | BadgeLineData | BadgeShapeData | BadgeQRData>

/** The element with an update merged in: its own fields, the rest into its data. */
const applyElementUpdate = (element: BadgeElement, updates: ElementUpdate) => {
  const {
    x, y, width, height, zIndex, fill, border, transforms, ...elementData
  } = updates

  return {
    ...element,
    ...(x !== undefined && { x }),
    ...(y !== undefined && { y }),
    ...(width !== undefined && { width }),
    ...(height !== undefined && { height }),
    ...(zIndex !== undefined && { zIndex }),
    ...(fill !== undefined && { fill }),
    ...(border !== undefined && { border }),
    ...(transforms !== undefined && { transforms }),
    ...(Object.keys(elementData).length > 0 && {
      data: { ...element.data, ...elementData },
    }),
  } as BadgeElement // The element type and its data type match at runtime
}

const useElementTransforms = (
  currentTemplate: BadgeTemplate | undefined,
  selectedElementIds: string[],
  pushState: (template: BadgeTemplate) => void,
) => {
  // Store in refs so callbacks stay stable across template changes
  const templateRef = useRef(currentTemplate)
  templateRef.current = currentTemplate
  const selectedIdsRef = useRef(selectedElementIds)
  selectedIdsRef.current = selectedElementIds

  const handleElementChange = useCallback((
    elementId: string,
    updates: ElementUpdate,
  ) => {
    const template = templateRef.current
    if (!template) return

    pushState(updateTemplateElements(template, (elements) => elements.map(
      (el) => (el.id === elementId ? applyElementUpdate(el, updates) : el),
    )))
  }, [pushState])

  /** Changes several elements as one step. */
  const handleElementsChange = useCallback((updates: Map<string, ElementUpdate>) => {
    const template = templateRef.current
    if (!template || updates.size === 0) return

    pushState(updateTemplateElements(template, (elements) => elements.map((el) => {
      const update = updates.get(el.id)

      return update ? applyElementUpdate(el, update) : el
    })))
  }, [pushState])

  // Arrow key movement functionality
  const handleMoveElement = useCallback((
    direction: "up" | "down" | "left" | "right",
    distance = 1,
  ) => {
    const template = templateRef.current
    const selIds = selectedIdsRef.current
    if (!template || selIds.length === 0) return

    const elementsToMove = template.badgeData.elements.filter(
      (el) => selIds.includes(el.id),
    )
    if (elementsToMove.length === 0) return

    const { width: templateWidth, height: templateHeight } = getTemplateDimensions(template)

    // Calculate the requested delta
    let deltaX = 0
    let deltaY = 0

    switch (direction) {
      case "left":
        deltaX = -distance
        break
      case "right":
        deltaX = distance
        break
      case "up":
        deltaY = -distance
        break
      case "down":
        deltaY = distance
        break
      default:
        return
    }

    // Only clamp if any non-shape element is in the selection
    const allShapes = elementsToMove.every((el) => canLeaveBadge(el.type))
    let effectiveDX = deltaX
    let effectiveDY = deltaY
    if (!allShapes) {
      const elemsWithRotation = elementsToMove.map((el) => ({
        x: el.x,
        y: el.y,
        width: el.width,
        height: el.height,
        rotation: el.transforms?.rotate || 0,
      }))
      const clamped = restrictMovementToBounds(
        deltaX, deltaY, elemsWithRotation, templateWidth, templateHeight,
      )
      effectiveDX = clamped.deltaX
      effectiveDY = clamped.deltaY
    }

    pushState(updateTemplateElements(template, (elements) => elements.map((element) => {
      if (!selIds.includes(element.id)) return element

      return {
        ...element,
        x: element.x + effectiveDX,
        y: element.y + effectiveDY,
      }
    })))
  }, [pushState])

  const handleBringToFront = useCallback((elementId: string) => {
    const template = templateRef.current
    if (!template || !elementId) return

    const sortedElements = [...template.badgeData.elements].sort(
      (a, b) => b.zIndex - a.zIndex,
    )
    const targetIndex = sortedElements.findIndex(
      (el) => el.id === elementId,
    )
    if (targetIndex <= 0) return

    const elementAbove = sortedElements[targetIndex - 1]
    const targetElement = sortedElements[targetIndex]

    pushState(updateTemplateElements(template, (elements) => elements.map((element) => {
      if (element.id === targetElement.id) {
        return { ...element, zIndex: elementAbove.zIndex }
      }
      if (element.id === elementAbove.id) {
        return { ...element, zIndex: targetElement.zIndex }
      }

      return element
    })))
  }, [pushState])

  const handleSendToBack = useCallback((elementId: string) => {
    const template = templateRef.current
    if (!template || !elementId) return

    const sortedElements = [...template.badgeData.elements].sort(
      (a, b) => b.zIndex - a.zIndex,
    )
    const targetIndex = sortedElements.findIndex(
      (el) => el.id === elementId,
    )
    if (targetIndex >= sortedElements.length - 1 || targetIndex === -1) return

    const elementBelow = sortedElements[targetIndex + 1]
    const targetElement = sortedElements[targetIndex]

    pushState(updateTemplateElements(template, (elements) => elements.map((element) => {
      if (element.id === targetElement.id) {
        return { ...element, zIndex: elementBelow.zIndex }
      }
      if (element.id === elementBelow.id) {
        return { ...element, zIndex: targetElement.zIndex }
      }

      return element
    })))
  }, [pushState])

  const updateElementZIndex = useCallback((elementId: string, newZIndex: number) => {
    const template = templateRef.current
    if (!template) return

    pushState(updateTemplateElements(
      template, (elements) => elements.map((element) => (element.id === elementId
        ? { ...element, zIndex: newZIndex } : element)),
    ))
  }, [pushState])

  const handleCommitReorder = useCallback((newElementsOrder: BadgeElement[]) => {
    const template = templateRef.current
    if (!template) return

    pushState(updateTemplateElements(template, (elements) => elements.map((element) => {
      const newIndex = newElementsOrder.findIndex((el) => el.id === element.id)

      return {
        ...element,
        zIndex: newElementsOrder.length - newIndex,
      }
    })))
  }, [pushState])

  return {
    handleElementChange,
    handleElementsChange,
    handleMoveElement,
    handleBringToFront,
    handleSendToBack,
    updateElementZIndex,
    handleCommitReorder,
  }
}

export default useElementTransforms
